/**
 * help-text-item.js
 */

import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { shouldRenderMobileLayout } from 'utils/app-context';


const useStyles = makeStyles(theme => ({
  hoverable: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 -100px',
    padding: props => `${props.noVerticalPadding ? 0 : theme.spacing(1)} 100px`,
    minHeight: theme.spacing(6),
    maxWidth: 'calc(100vw + 200px - 68px)', // calculated to prevent overflow on mobile
    transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    'body:not(.help-text-focused) &:hover': {
      backgroundColor: alpha(theme.palette.grey.colors[1], 0.04),
      [theme.breakpoints.down('sm_md')]: {
        backgroundColor: 'transparent !important'
      },
      '& .accordion-highlighted:hover': {
        backgroundColor: 'transparent',
      }
    },

    // custom editor input styles
    '& .MuiTextField-root, .MuiFormControl-root': {
      width: '100%',
      maxWidth: '100%',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    '& .MuiTextField-root': {
      marginTop: theme.spacing(0.5),
    },
    '& .MuiFormLabel-root b': {
      fontWeight: '500',
    },
    '& .MuiFormControlLabel-root .MuiCheckbox-root': {
      marginLeft: shouldRenderMobileLayout() ? 0 : theme.spacing(0.5),
    },
    '& .MuiFormControl-root .MuiFormHelperText-root': {
      lineHeight: '1.2em',
    },
    '& .MuiFormGroup-root': {
    },
  },
  minimalHover: {
    margin: `0 -${theme.spacing(1)}`,
    padding: props => theme.spacing(1),
    borderRadius: 4,
    maxWidth: '100%',
    boxSizing: 'content-box',
    justifyContent: 'flex-start',
    minHeight: theme.spacing(5),
  },
  highlight: {
    backgroundColor: alpha(theme.palette.primary.colors[2], 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.colors[2], 0.08),
    }
  },
}));

export default function HelpTextItem(props) {
  const {
    sidebarText,
    title,
    text,
    children,
    className,
    style,
    // disableFocusHover,
    minimalHoverStyle,
    noVerticalPadding,
    ...extraProps
  } = props;
  const classes = useStyles({ noVerticalPadding });
  const [focus, setFocus] = React.useState(false);


  const onHover = () => {
    sidebarText.text.set({ title, info: text });
  }


  // if chidren is a function then call it with focus handlers otherwise render as normal
  const newChildren = React.useMemo(() => {

    const onFocus = (isFocused) => {
      // if focus val is true we first have to reset focus to false so sidebar text will update.
      // otherwise it may prevent updating if it is already focused.
      if (isFocused) {
        sidebarText.isFocused.set(false);
        sidebarText.text.set({ title, info: text });
        document.body.classList.add('help-text-focused');
      } else {
        document.body.classList.remove('help-text-focused');
      }

      setFocus(isFocused);
      setTimeout(() => sidebarText.isFocused.set(isFocused));
    }

    return (
      <>
        {typeof children === 'function'
          ? children({
            onFocus: () => onFocus(true),
            onBlur: () => onFocus(false)
          })
          : children}
      </>
    );

  // sidebarText is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, title, text]);


  return (
    <>
      <Box
        className={clsx(
          classes.hoverable,
          focus && classes.highlight,
          minimalHoverStyle && classes.minimalHover,
          className
        )}
        style={style}
        onMouseOver={() => title ? onHover() : null}
        {...extraProps}
      >
        {newChildren}
      </Box>
    </>
  );
}